import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Upload,
  Loader2,
  Image as ImageIcon,
  Trash2,
  CheckCircle2,
  FolderPlus,
  Folder as FolderIcon,
  ChevronRight,
  Home as HomeIcon,
  MoreVertical,
  Pencil,
  Info,
  Move,
  ArrowUp,
  ArrowDownUp,
  ArrowUpAZ,
  ArrowDownAZ,
  Keyboard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { listMediaFiltered, type MediaFilteredRow } from "@/lib/media-manager";

const BUCKET = "product-media";
const FOLDER_PLACEHOLDER = ".emptyFolderPlaceholder";

type Entry = {
  name: string;
  fullPath: string;
  isFolder: boolean;
  url: string | null;
  size: number;
  contentType: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

const joinPath = (a: string, b: string) => (a ? `${a.replace(/\/+$/, "")}/${b}` : b);

type FilterArgs = {
  prefix: string;
  search: string;
  type: "all" | "folder" | "image" | "other";
  size: "all" | "lt100kb" | "100kb-1mb" | "1mb-5mb" | "gt5mb";
  updated: "all" | "today" | "7d" | "30d" | "older";
  sortBy: "name" | "size" | "updated";
  sortDir: "asc" | "desc";
};

async function listMedia(prefix: string): Promise<Entry[]> {
  // Lightweight non-filtered listing used as a fallback (and elsewhere in the file).
  const { data, error } = await supabase.storage.from(BUCKET).list(prefix, {
    limit: 500,
    sortBy: { column: "name", order: "asc" },
  });
  if (error) throw error;
  return (data ?? [])
    .filter((f) => f.name !== FOLDER_PLACEHOLDER)
    .map((f) => {
      const isFolder = f.id === null;
      const fullPath = joinPath(prefix, f.name);
      return {
        name: f.name,
        fullPath,
        isFolder,
        url: isFolder ? null : supabase.storage.from(BUCKET).getPublicUrl(fullPath).data.publicUrl,
        size: (f.metadata as { size?: number } | null)?.size ?? 0,
        contentType: (f.metadata as { mimetype?: string } | null)?.mimetype ?? null,
        createdAt: f.created_at ?? null,
        updatedAt: f.updated_at ?? null,
      };
    })
    .sort((a, b) =>
      a.isFolder === b.isFolder ? a.name.localeCompare(b.name) : a.isFolder ? -1 : 1,
    );
}

async function listMediaServer(args: FilterArgs): Promise<Entry[]> {
  // Normalise prefix so trailing-slash matches storage layout (no slash for root).
  const prefix = args.prefix ? args.prefix.replace(/\/+$/, "") + "/" : "";
  const rows = await listMediaFiltered({
    prefix,
    search: args.search.trim(),
    type: args.type,
    size: args.size,
    updated: args.updated,
    sortBy: args.sortBy,
    sortDir: args.sortDir,
    limit: 1000,
  });
  return (rows as MediaFilteredRow[]).map((r) => ({
    name: r.name,
    fullPath: r.full_path,
    isFolder: r.is_folder,
    url: r.is_folder
      ? null
      : supabase.storage.from(BUCKET).getPublicUrl(r.full_path).data.publicUrl,
    size: typeof r.size === "string" ? Number(r.size) : (r.size ?? 0),
    contentType: r.content_type,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  }));
}

async function listAllUnder(prefix: string): Promise<string[]> {
  // Recursively list every file path under a prefix.
  const out: string[] = [];
  const stack: string[] = [prefix];
  while (stack.length) {
    const cur = stack.pop()!;
    const { data, error } = await supabase.storage.from(BUCKET).list(cur, {
      limit: 1000,
    });
    if (error) throw error;
    for (const item of data ?? []) {
      const path = joinPath(cur, item.name);
      if (item.id === null) stack.push(path);
      else out.push(path);
    }
  }
  return out;
}

interface Props {
  onSelect: (url: string) => void;
  onClose: () => void;
}

export function MediaManager({ onSelect, onClose }: Props) {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<
    Array<{ name: string; pct: number; status: "uploading" | "done" | "error"; error?: string }>
  >([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "folder" | "image" | "other">("all");
  const [sizeFilter, setSizeFilter] = useState<
    "all" | "lt100kb" | "100kb-1mb" | "1mb-5mb" | "gt5mb"
  >("all");
  const [updatedFilter, setUpdatedFilter] = useState<"all" | "today" | "7d" | "30d" | "older">(
    "all",
  );
  const [sortBy, setSortBy] = useState<"name" | "size" | "updated">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selected, setSelected] = useState<string | null>(null);
  const [path, setPath] = useState("");
  const [dragOverPath, setDragOverPath] = useState<string | null>(null);
  const [infoEntry, setInfoEntry] = useState<Entry | null>(null);
  const [renaming, setRenaming] = useState<{ entry: Entry; value: string } | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const isStaff = profile?.role === "staff" || profile?.role === "admin";

  // Server-side filtering + sorting via the list_media_filtered RPC. The DB does
  // the heavy work so large folders only ship the matching rows to the browser.
  const { data: filtered = [], isLoading } = useQuery({
    enabled: !!profile && isStaff,
    queryKey: [
      "media-manager",
      path,
      search.trim(),
      typeFilter,
      sizeFilter,
      updatedFilter,
      sortBy,
      sortDir,
    ],
    queryFn: () =>
      listMediaServer({
        prefix: path,
        search,
        type: typeFilter,
        size: sizeFilter,
        updated: updatedFilter,
        sortBy,
        sortDir,
      }),
    placeholderData: (prev) => prev,
  });

  const filtersActive =
    search.trim() !== "" || typeFilter !== "all" || sizeFilter !== "all" || updatedFilter !== "all";
  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setSizeFilter("all");
    setUpdatedFilter("all");
  };

  // Keyboard shortcuts: "/" focuses the search box, "Esc" clears filters
  // (or closes the manager when nothing is filtered/selected).
  useEffect(() => {
    const isTypingTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (e.key === "Escape" && filtersActive && !isTypingTarget(e.target)) {
        e.preventDefault();
        clearFilters();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtersActive]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["media-manager"] });

  const handleUpload = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList?.length) return;
      const MAX_BYTES = 25 * 1024 * 1024; // 25 MB per file
      const files = Array.from(fileList);
      // Pre-validate
      const initial = files.map((f) => {
        if (f.size > MAX_BYTES) {
          return {
            name: f.name,
            pct: 0,
            status: "error" as const,
            error: `File is ${(f.size / 1024 / 1024).toFixed(1)} MB — max 25 MB`,
          };
        }
        if (!f.type.startsWith("image/") && f.type !== "application/pdf") {
          return {
            name: f.name,
            pct: 0,
            status: "error" as const,
            error: `Unsupported type (${f.type || "unknown"}). Images and PDF only.`,
          };
        }
        return { name: f.name, pct: 0, status: "uploading" as const };
      });
      setUploadQueue(initial);
      setUploading(true);
      let ok = 0;
      let failed = 0;
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (initial[i].status === "error") {
            failed++;
            toast.error(`${file.name}: ${initial[i].error}`);
            continue;
          }
          const objectPath = joinPath(path, `${Date.now()}-${file.name.replace(/\s+/g, "_")}`);
          // Tick a fake progress while the upload is in flight — Supabase JS
          // doesn't expose XHR progress for storage.upload. We flip to 100%
          // when the promise resolves.
          const tick = window.setInterval(() => {
            setUploadQueue((q) =>
              q.map((row, idx) =>
                idx === i && row.status === "uploading"
                  ? { ...row, pct: Math.min(90, row.pct + 8) }
                  : row,
              ),
            );
          }, 250);
          const { error } = await supabase.storage
            .from(BUCKET)
            .upload(objectPath, file, { upsert: false, contentType: file.type || undefined });
          window.clearInterval(tick);
          if (error) {
            failed++;
            setUploadQueue((q) =>
              q.map((row, idx) =>
                idx === i ? { ...row, status: "error", error: error.message } : row,
              ),
            );
            toast.error(`${file.name}: ${error.message}`);
            continue;
          }
          ok++;
          setUploadQueue((q) =>
            q.map((row, idx) => (idx === i ? { ...row, pct: 100, status: "done" } : row)),
          );
        }
        if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
        if (failed > 0 && ok === 0)
          toast.error(`${failed} upload${failed === 1 ? "" : "s"} failed`);
        refresh();
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
        // Auto-clear successful entries after a short delay; keep errors visible.
        window.setTimeout(() => {
          setUploadQueue((q) => q.filter((row) => row.status === "error"));
        }, 2500);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path],
  );

  const handleDelete = async (entry: Entry) => {
    const ok = confirm(
      entry.isFolder
        ? `Delete folder "${entry.name}" and everything inside?`
        : `Delete "${entry.name}"?`,
    );
    if (!ok) return;
    try {
      const paths = entry.isFolder ? await listAllUnder(entry.fullPath) : [entry.fullPath];
      // Also remove any folder placeholders
      const placeholders = entry.isFolder ? [joinPath(entry.fullPath, FOLDER_PLACEHOLDER)] : [];
      const all = [...paths, ...placeholders];
      if (all.length) {
        const { error } = await supabase.storage.from(BUCKET).remove(all);
        if (error) throw error;
      }
      if (selected === entry.url) setSelected(null);
      refresh();
      toast.success("Deleted");
    } catch (e) {
      toast.error((e as Error).message ?? "Delete failed");
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim().replace(/[/\\]/g, "");
    if (!name) return;
    try {
      const placeholderPath = joinPath(joinPath(path, name), FOLDER_PLACEHOLDER);
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(placeholderPath, new Blob([""]), { upsert: true });
      if (error) throw error;
      toast.success("Folder created");
      setCreatingFolder(false);
      setNewFolderName("");
      refresh();
    } catch (e) {
      toast.error((e as Error).message ?? "Could not create folder");
    }
  };

  const moveEntry = async (entry: Entry, destPrefix: string) => {
    if (destPrefix === path && !entry.isFolder) return;
    const destFullPath = joinPath(destPrefix, entry.name);
    if (destFullPath === entry.fullPath) return;
    if (entry.isFolder && destFullPath.startsWith(`${entry.fullPath}/`)) {
      toast.error("Can't move a folder into itself");
      return;
    }
    try {
      if (entry.isFolder) {
        const sources = await listAllUnder(entry.fullPath);
        for (const src of sources) {
          const rel = src.slice(entry.fullPath.length + 1);
          const dst = joinPath(destFullPath, rel);
          const { error } = await supabase.storage.from(BUCKET).move(src, dst);
          if (error) throw error;
        }
        // Re-create placeholder so empty folder still exists at destination
        await supabase.storage
          .from(BUCKET)
          .upload(joinPath(destFullPath, FOLDER_PLACEHOLDER), new Blob([""]), {
            upsert: true,
          });
        // Remove old placeholder if it existed
        await supabase.storage.from(BUCKET).remove([joinPath(entry.fullPath, FOLDER_PLACEHOLDER)]);
      } else {
        const { error } = await supabase.storage.from(BUCKET).move(entry.fullPath, destFullPath);
        if (error) throw error;
      }
      toast.success("Moved");
      refresh();
    } catch (e) {
      toast.error((e as Error).message ?? "Move failed");
    }
  };

  const doRename = async (entry: Entry, newName: string) => {
    const clean = newName.trim().replace(/[/\\]/g, "");
    if (!clean || clean === entry.name) {
      setRenaming(null);
      return;
    }
    const parent = entry.fullPath.includes("/")
      ? entry.fullPath.slice(0, entry.fullPath.lastIndexOf("/"))
      : "";
    const dest = joinPath(parent, clean);
    try {
      if (entry.isFolder) {
        const sources = await listAllUnder(entry.fullPath);
        for (const src of sources) {
          const rel = src.slice(entry.fullPath.length + 1);
          const dst = joinPath(dest, rel);
          const { error } = await supabase.storage.from(BUCKET).move(src, dst);
          if (error) throw error;
        }
        await supabase.storage
          .from(BUCKET)
          .upload(joinPath(dest, FOLDER_PLACEHOLDER), new Blob([""]), { upsert: true });
        await supabase.storage.from(BUCKET).remove([joinPath(entry.fullPath, FOLDER_PLACEHOLDER)]);
      } else {
        const { error } = await supabase.storage.from(BUCKET).move(entry.fullPath, dest);
        if (error) throw error;
      }
      toast.success("Renamed");
      setRenaming(null);
      refresh();
    } catch (e) {
      toast.error((e as Error).message ?? "Rename failed");
    }
  };

  const breadcrumbs = useMemo(() => {
    const parts = path ? path.split("/").filter(Boolean) : [];
    const crumbs: { label: string; path: string }[] = [{ label: "Home", path: "" }];
    let acc = "";
    for (const p of parts) {
      acc = acc ? `${acc}/${p}` : p;
      crumbs.push({ label: p, path: acc });
    }
    return crumbs;
  }, [path]);

  const parentPath = useMemo(() => {
    if (!path) return null;
    return path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
  }, [path]);

  const onDragStart = (e: React.DragEvent, entry: Entry) => {
    e.dataTransfer.setData("application/x-media-entry", JSON.stringify(entry));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDropTo = async (e: React.DragEvent, destPrefix: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPath(null);
    const raw = e.dataTransfer.getData("application/x-media-entry");
    if (raw) {
      try {
        const entry = JSON.parse(raw) as Entry;
        await moveEntry(entry, destPrefix);
      } catch {
        // ignore
      }
      return;
    }
    if (e.dataTransfer.files?.length) {
      // Fallback: external file drop into specific folder = upload into current path only
      handleUpload(e.dataTransfer.files);
    }
  };

  if (!isStaff) return null;

  const panel = (
    <motion.div
      key="bd"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        key="dlg"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ type: "spring", damping: 28, stiffness: 320 }}
        className="relative flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-[2rem] border border-border bg-card shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">Media Manager</span>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
          <Input
            ref={searchRef}
            placeholder='Search in this folder…  ( press "/" )'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                if (search) {
                  e.stopPropagation();
                  setSearch("");
                }
                searchRef.current?.blur();
              }
            }}
            className="h-8 rounded-full text-sm w-64"
          />
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
            <SelectTrigger className="h-8 w-32 rounded-full text-xs">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="folder">Folders</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="other">Other files</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sizeFilter} onValueChange={(v) => setSizeFilter(v as typeof sizeFilter)}>
            <SelectTrigger className="h-8 w-36 rounded-full text-xs">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any size</SelectItem>
              <SelectItem value="lt100kb">&lt; 100 KB</SelectItem>
              <SelectItem value="100kb-1mb">100 KB – 1 MB</SelectItem>
              <SelectItem value="1mb-5mb">1 – 5 MB</SelectItem>
              <SelectItem value="gt5mb">&gt; 5 MB</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={updatedFilter}
            onValueChange={(v) => setUpdatedFilter(v as typeof updatedFilter)}
          >
            <SelectTrigger className="h-8 w-36 rounded-full text-xs">
              <SelectValue placeholder="Updated" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="today">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="older">Older than 30 days</SelectItem>
            </SelectContent>
          </Select>
          {filtersActive && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 rounded-full text-xs"
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
          <div className="flex items-center gap-1">
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="h-8 w-32 rounded-full text-xs">
                <ArrowDownUp className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="size">File size</SelectItem>
                <SelectItem value="updated">Updated</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 rounded-full p-0"
              title={`Sort ${sortDir === "asc" ? "ascending" : "descending"}`}
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            >
              {sortDir === "asc" ? (
                <ArrowUpAZ className="h-3.5 w-3.5" />
              ) : (
                <ArrowDownAZ className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              Press
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                /
              </kbd>
              to search,
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                Esc
              </kbd>
              to clear
            </span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 rounded-full p-0"
                  title="Keyboard shortcuts"
                  aria-label="Keyboard shortcuts"
                >
                  <Keyboard className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 rounded-2xl p-0">
                <div className="border-b border-border px-4 py-2.5 flex items-center gap-2">
                  <Keyboard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Keyboard shortcuts</span>
                </div>
                <div className="max-h-80 overflow-y-auto px-4 py-3 space-y-4">
                  {[
                    {
                      group: "Search & filters",
                      items: [
                        { keys: ["/"], label: "Focus the search box" },
                        { keys: ["Esc"], label: "Clear all active filters" },
                        { keys: ["Esc"], label: "Clear search text (when focused)" },
                      ],
                    },
                    {
                      group: "Navigation",
                      items: [
                        { keys: ["Click"], label: "Open a folder" },
                        { keys: ["Breadcrumb"], label: "Jump to a parent folder" },
                        { keys: ["Esc"], label: "Close the manager (when no filters)" },
                      ],
                    },
                    {
                      group: "Selection & actions",
                      items: [
                        { keys: ["Click"], label: "Select a file or folder" },
                        { keys: ["Enter"], label: "Confirm rename / create folder" },
                        { keys: ["Esc"], label: "Cancel rename / new folder" },
                      ],
                    },
                  ].map((section) => (
                    <div key={section.group}>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {section.group}
                      </p>
                      <ul className="space-y-1.5">
                        {section.items.map((item, i) => (
                          <li
                            key={`${section.group}-${i}`}
                            className="flex items-center justify-between gap-3 text-xs"
                          >
                            <span className="text-foreground/90">{item.label}</span>
                            <span className="flex items-center gap-1">
                              {item.keys.map((k) => (
                                <kbd
                                  key={k}
                                  className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md border border-border bg-muted px-1.5 font-mono text-[10px] text-foreground shadow-sm"
                                >
                                  {k}
                                </kbd>
                              ))}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full gap-1.5 h-8"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              Upload
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full gap-1.5 h-8"
              onClick={() => setCreatingFolder(true)}
            >
              <FolderPlus className="h-3.5 w-3.5" /> New folder
            </Button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>

        {/* Upload progress strip */}
        {uploadQueue.length > 0 && (
          <div className="border-b border-border bg-muted/30 px-6 py-3 space-y-2">
            {uploadQueue.map((row, i) => (
              <div key={`${row.name}-${i}`} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{row.name}</span>
                  <span
                    className={cn(
                      "shrink-0 tabular-nums",
                      row.status === "error" && "text-destructive",
                      row.status === "done" && "text-emerald-600 dark:text-emerald-400",
                      row.status === "uploading" && "text-muted-foreground",
                    )}
                  >
                    {row.status === "error"
                      ? "Failed"
                      : row.status === "done"
                        ? "Uploaded"
                        : `${row.pct}%`}
                  </span>
                </div>
                {row.status === "error" ? (
                  <p className="mt-0.5 text-[11px] text-destructive">{row.error}</p>
                ) : (
                  <Progress value={row.pct} className="mt-1 h-1.5" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Breadcrumbs */}
        <div
          className="flex items-center gap-1 border-b border-border px-6 py-2 text-xs"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOverPath("__breadcrumbs__");
          }}
          onDragLeave={() => setDragOverPath(null)}
        >
          {breadcrumbs.map((c, i) => {
            const isLast = i === breadcrumbs.length - 1;
            const isHover = dragOverPath === `crumb:${c.path}`;
            return (
              <div key={c.path} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                <button
                  onClick={() => setPath(c.path)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverPath(`crumb:${c.path}`);
                  }}
                  onDragLeave={() => setDragOverPath(null)}
                  onDrop={(e) => onDropTo(e, c.path)}
                  className={cn(
                    "rounded-full px-2 py-0.5 transition-colors",
                    isLast
                      ? "font-semibold text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                    isHover && "bg-primary/10 text-primary",
                  )}
                >
                  {i === 0 ? <HomeIcon className="h-3 w-3 inline" /> : c.label}
                </button>
              </div>
            );
          })}
          {parentPath !== null && (
            <button
              onClick={() => setPath(parentPath)}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverPath("up");
              }}
              onDragLeave={() => setDragOverPath(null)}
              onDrop={(e) => onDropTo(e, parentPath)}
              className={cn(
                "ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:text-foreground hover:bg-muted",
                dragOverPath === "up" && "bg-primary/10 text-primary border-primary",
              )}
            >
              <ArrowUp className="h-3 w-3" /> Up
            </button>
          )}
        </div>

        {/* New folder inline form */}
        {creatingFolder && (
          <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-6 py-2">
            <FolderIcon className="h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createFolder();
                if (e.key === "Escape") {
                  setCreatingFolder(false);
                  setNewFolderName("");
                }
              }}
              placeholder="Folder name"
              className="h-8 text-sm w-64"
            />
            <Button size="sm" className="h-8 rounded-full" onClick={createFolder}>
              Create
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 rounded-full"
              onClick={() => {
                setCreatingFolder(false);
                setNewFolderName("");
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Grid */}
        <div
          className={cn(
            "flex-1 overflow-y-auto p-5 transition-colors",
            dragOverPath === path && "bg-primary/5",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOverPath(path);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) setDragOverPath(null);
          }}
          onDrop={(e) => {
            // Drop into current folder = move there OR upload external files
            onDropTo(e, path);
          }}
        >
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border text-center">
              <Upload className="mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {filtersActive ? "No results" : "Empty folder — drag & drop or click Upload"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
              {filtered.map((f) => {
                const isFolder = f.isFolder;
                const isHoverDrop = dragOverPath === `folder:${f.fullPath}`;
                const isSelected = !isFolder && selected === f.url;
                return (
                  <div
                    key={f.fullPath}
                    draggable
                    onDragStart={(e) => onDragStart(e, f)}
                    onDragOver={(e) => {
                      if (isFolder) {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverPath(`folder:${f.fullPath}`);
                      }
                    }}
                    onDragLeave={() => isFolder && setDragOverPath(null)}
                    onDrop={(e) => {
                      if (isFolder) onDropTo(e, f.fullPath);
                    }}
                    onDoubleClick={() => isFolder && setPath(f.fullPath)}
                    className={cn(
                      "group relative cursor-pointer overflow-hidden rounded-xl border-2 transition-all",
                      isSelected
                        ? "border-primary shadow-soft"
                        : isHoverDrop
                          ? "border-primary bg-primary/10"
                          : "border-transparent hover:border-border",
                    )}
                    onClick={() => {
                      if (isFolder) setPath(f.fullPath);
                      else setSelected(f.url);
                    }}
                  >
                    <div className="aspect-square bg-muted">
                      {isFolder ? (
                        <div className="grid h-full w-full place-items-center bg-gradient-to-br from-muted to-muted/40">
                          <FolderIcon className="h-12 w-12 text-primary/70" />
                        </div>
                      ) : (
                        <img
                          src={f.url ?? ""}
                          alt={f.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </div>
                    {isSelected && (
                      <div className="absolute right-1.5 top-1.5 rounded-full bg-primary p-0.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary-foreground" />
                      </div>
                    )}
                    {/* Per-item menu */}
                    <div className="absolute right-1.5 top-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="rounded-full bg-background/90 p-1 shadow-sm hover:bg-background"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => setInfoEntry(f)}>
                            <Info className="h-3.5 w-3.5 mr-2" /> File info
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setRenaming({ entry: f, value: f.name })}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                          </DropdownMenuItem>
                          {!isFolder && f.url && (
                            <DropdownMenuItem
                              onClick={() => {
                                navigator.clipboard.writeText(f.url ?? "");
                                toast.success("URL copied");
                              }}
                            >
                              <Move className="h-3.5 w-3.5 mr-2" /> Copy URL
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDelete(f)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {renaming?.entry.fullPath === f.fullPath ? (
                      <input
                        autoFocus
                        value={renaming.value}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenaming({ entry: f, value: e.target.value })}
                        onBlur={() => doRename(f, renaming.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") doRename(f, renaming.value);
                          if (e.key === "Escape") setRenaming(null);
                        }}
                        className="w-full border-t border-border bg-background px-1 py-0.5 text-[10px]"
                      />
                    ) : (
                      <p className="truncate px-1 py-0.5 text-[10px] text-muted-foreground">
                        {isFolder ? f.name : f.name.replace(/^\d+-/, "")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {selected
              ? "1 image selected"
              : `${filtered.filter((e) => !e.isFolder).length} image(s) shown · ${filtered.filter((e) => e.isFolder).length} folder(s)`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="rounded-full bg-gradient-hero text-primary-foreground hover:opacity-90"
              disabled={!selected}
              onClick={() => {
                if (selected) {
                  onSelect(selected);
                  onClose();
                }
              }}
            >
              Use image
            </Button>
          </div>
        </div>

        {/* File info modal */}
        <AnimatePresence>
          {infoEntry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm p-6"
              onClick={() => setInfoEntry(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-pop"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Info className="h-4 w-4" /> File info
                  </h3>
                  <button
                    className="rounded-full p-1 hover:bg-muted"
                    onClick={() => setInfoEntry(null)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {!infoEntry.isFolder && infoEntry.url && (
                  <img
                    src={infoEntry.url}
                    alt={infoEntry.name}
                    className="mb-3 max-h-40 w-full rounded-lg border border-border object-contain bg-muted"
                  />
                )}
                <dl className="grid grid-cols-[100px_1fr] gap-y-1.5 text-xs">
                  <dt className="text-muted-foreground">Name</dt>
                  <dd className="font-mono break-all">{infoEntry.name}</dd>
                  <dt className="text-muted-foreground">Type</dt>
                  <dd>{infoEntry.isFolder ? "Folder" : (infoEntry.contentType ?? "—")}</dd>
                  <dt className="text-muted-foreground">Path</dt>
                  <dd className="font-mono break-all">/{infoEntry.fullPath}</dd>
                  {!infoEntry.isFolder && (
                    <>
                      <dt className="text-muted-foreground">Size</dt>
                      <dd>{(infoEntry.size / 1024).toFixed(1)} KB</dd>
                      <dt className="text-muted-foreground">URL</dt>
                      <dd className="font-mono break-all text-primary">
                        <a
                          href={infoEntry.url ?? "#"}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          {infoEntry.url}
                        </a>
                      </dd>
                    </>
                  )}
                  {infoEntry.createdAt && (
                    <>
                      <dt className="text-muted-foreground">Created</dt>
                      <dd>{new Date(infoEntry.createdAt).toLocaleString()}</dd>
                    </>
                  )}
                  {infoEntry.updatedAt && (
                    <>
                      <dt className="text-muted-foreground">Updated</dt>
                      <dd>{new Date(infoEntry.updatedAt).toLocaleString()}</dd>
                    </>
                  )}
                </dl>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );

  return createPortal(<AnimatePresence>{panel}</AnimatePresence>, document.body);
}
